import chroma from 'chroma-js';
import { cloneDeep, get } from 'lodash';

const baseColors = {
  bodyBackground: '#fff',
  materialAppBarBackground: '#3819A3',
  none: 'transparent',
  primaryVariant: '#353535',
  primary: '#6E6E6E',
  materialPrimary: '#623FD0',
  materialPrimaryDisabled: '#E0E0E0',
  secondary: '#C1E9EB',
  materialSecondary: '#FFFFFF',
  secondaryVariant: '#CCD32B',
  buttonSupport: '#D3D3D3',
  primaryBackground: '#FAFAFD',
  darkBlue: '#126BC5',
  box: '#78D5D7',
  error: '#E25257',
  success: '#A2D9A2',
  textNegative: '#FFFFFF',
  textLabel: '#353535',
  primaryLink: '#0B68B8',
  textPositive: '#474747',
  textMuted: '#696969',
  borderSupport: '#D9DDE0',
  secondaryBackground: '#FFFFFF',
  reserve01: '#c1c5c7',
  reserve02: '#C0C3C7',
  reserve03: '#dcdcdc',
  neutralAlert: '#FF9429',
  focus: '#3F88C5',
  materialPrimaryLink: '#000000',
  black: '#000',
  white: '#fff',
  tableBackground: '#e4e4e4',
  tableSecondary: '#383838',
};

/**
 * @param {string} color
 * @param {function} conversion
 * @param {number} amount
 */
export const applyChroma = (color, conversion, amount) => {
  try {
    const chromaColor = chroma(color);
    if (conversion in chromaColor) {
      return chromaColor[conversion](Number(amount)).hex();
    }
    console.warn(`Unsupported conversion: ${conversion}`);
  } catch (error) {
    console.error(error);
  }
  return color;
};

export const interpolateTheme = (theme, srcTheme) => {
  if (!theme) {
    return theme;
  }
  if (!srcTheme) {
    srcTheme = theme;
  }
  const resultTheme = cloneDeep(theme);
  Object.entries(theme).forEach(([key, value]) => {
    if (key === 'fonts') {
      return;
    }
    // if value is an object, update it recursively
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      resultTheme[key] = interpolateTheme(value, srcTheme);
      return;
    }
    // if value is string, try to get a value from theme that matches the string - colors.primary, spacing.xxs etc.
    if (typeof value === 'string') {
      // hoverColor: "colors.secondary|chroma|darken|0.7"
      const [path, filter, ...filterArgs] = value.split('|');
      let subValue = get(srcTheme, path);
      if (filter && filter === 'chroma') {
        subValue = applyChroma(subValue, ...filterArgs);
      }
      if (subValue) {
        resultTheme[key] = subValue;
        return;
      }
    }
    // just return value
    resultTheme[key] = value;
  });
  return resultTheme;
};

export default {
  breakpoints: { xs: 0, sm: 665, md: 960, lg: 1280, xl: 1920 },
  spacing: {
    none: '0',
    xxs: '0.2rem',
    xs: '0.4rem',
    sm: '1rem',
    md: '2.4rem',
    lg: '3.6rem',
    xl: '4rem',
  },
  colors: { ...baseColors },
  fonts: [{ font: `Roboto`, weights: [100, '100i', 300, '300i', 400, '400i', 600, '600i', 700, '700i', 800, '800i'] }],
  favicon: {
    url: ``,
    alertCount: null,
    animated: true,
    animationDelay: 500,
  },
  typography: {
    primary: {
      color: 'colors.primary',
    },
    secondary: {
      color: 'colors.secondary',
    },
    textPrimary: {
      color: 'colors.textPositive',
    },
    textSecondary: {
      color: 'colors.textNegative',
    },
    error: {
      color: 'colors.error|chroma|brighten|0.5',
    },
    third: {
      color: 'colors.secondaryVariant',
    },
  },
  body: {
    backgroundColor: 'colors.bodyBackground',
    sm: {
      backgroundColor: 'colors.materialAppBarBackground',
    },
  },
  paragraph: {
    light: {
      color: 'colors.textNegative',
    },
    dark: {
      color: 'colors.textPositive',
    },
  },
  slider: {
    trackColor: 'colors.box',
    railColor: 'colors.reserve03',
    handleColor: 'colors.textPositive',
    background: 'colors.secondaryBackground',
  },
  inputNumber: {
    border: 'colors.borderSupport',
  },
  asyncDropdown: {
    control: {
      border: 'colors.borderSupport',
      focused: {
        border: 'colors.borderSupport',
      },
      valid: {
        border: 'colors.success',
      },
      invalid: {
        border: 'colors.error',
      },
    },
    list: {
      background: 'colors.primaryBackground',
      border: 'colors.borderSupport',
      color: 'colors.primaryVariant',
    },
    option: {
      hoverBackground: 'colors.buttonSupport',
    },
  },
  alerts: {
    success: {
      color: 'colors.textNegative',
      background: 'colors.success',
      border: 'colors.success',
      link: 'colors.textNegative',
    },
    info: {
      color: 'colors.textNegative',
      background: 'colors.focus',
      border: 'colors.focus',
      link: 'colors.textNegative',
    },
    error: {
      color: 'colors.textNegative',
      background: 'colors.error',
      border: 'colors.error',
      link: 'colors.textNegative',
    },
    warning: {
      color: 'colors.textNegative',
      background: 'colors.neutralAlert',
      border: 'colors.neutralAlert',
      link: 'colors.textNegative',
    },
  },
  alertUi: {
    color: 'colors.textNegative',
    background: 'colors.error',
    border: 'colors.textNegative',
    link: 'colors.textNegative',
    success: {
      color: 'colors.background',
      background: 'colors.success',
      border: 'colors.success',
    },
    info: {
      color: 'colors.primary',
      background: 'colors.focus',
      border: 'colors.focus',
    },
    error: {
      color: 'colors.textNegative',
      background: 'colors.error',
      border: 'colors.error',
    },
    warning: {
      color: 'colors.textNegative',
      background: 'colors.neutralAlert',
      border: 'colors.neutralAlert',
    },
  },
  tableUi: {
    head: {
      background: 'colors.reserve03',
      color: 'colors.primaryVariant',
    },
    body: {
      background: 'colors.secondaryBackground',
      backgroundHover: 'colors.primaryBackground',
      color: 'colors.primaryVariant',
      invalid: 'colors.error',
      edit: {
        color: 'colors.buttonSupport',
      },
    },
    NoDataMessage: {
      background: 'colors.secondaryBackground',
      color: 'colors.primary',
    },
    blurred: 'colors.reserve02',
  },
  table: {
    header: 'colors.reserve03',
    rows: 'colors.secondaryBackground',
    color: 'colors.primaryVariant',
    border: 'colors.borderSupport',
    invalidRow: 'colors.error',
    mobile: {
      title: 'colors.primary',
      edit: {
        color: 'colors.borderSupport',
      },
    },
    body: {
      background: 'colors.secondaryBackground',
      backgroundHover: 'colors.primaryBackground',
      color: 'colors.primaryVariant',
      invalid: 'colors.error',
      edit: {
        color: 'colors.textNegative',
      },
    },
    NoDataMessage: {
      background: 'colors.secondaryBackground',
      color: 'colors.primary',
    },
    blurred: 'colors.primary',
  },
  uploadUi: {
    background: 'colors.primaryBackground',
    border: 'colors.buttonSupport',
    color: 'colors.primary',
    disabled: {
      color: 'colors.buttonSupport',
    },
    dragOver: {
      border: 'colors.secondary',
    },
    icon: {
      fill: 'colors.box',
    },
  },
  uploadDocButton: {
    background: 'colors.success',
    color: 'colors.textLabel',
    border: 'colors.textNegative',
  },
  button: {
    default: {
      background: 'colors.secondary',
      color: 'colors.textPositive',
      hoverBackground: 'colors.secondary|chroma|darker|0.7',
    },

    primary: {
      color: 'colors.primary',
      hoverBackground: 'colors.buttonSupport|chroma|darker|0.7',
      border: 'colors.primaryVariant',
      background: 'colors.buttonSupport',
    },
    secondary: {
      background: 'colors.box',
      color: 'colors.primaryVariant',
      hoverBackground: 'colors.secondary|chroma|darker|0.7',
    },
    disabled: {
      background: 'colors.buttonSupport|chroma|brighten|0.5',
      color: 'colors.buttonSupport',
      border: 'colors.buttonSupport|chroma|brighten|0.5',
    },
    actionButton: {
      background: 'colors.secondary',
    },
    outlined: {
      color: 'colors.box',
    },
  },
  datePicker: {
    picker: {
      background: 'colors.textNegative',
      color: 'colors.textPositive',
      border: 'colors.buttonSupport',
      header: {
        background: 'colors.secondary',
        color: 'colors.textPositive',
      },
      days: {
        background: 'colors.textNegative',
        color: 'colors.textPositive',
      },
      day: {
        color: 'colors.textPositive',
        hoverColor: 'colors.textPositive',
        hoverBackground: 'colors.reserve02',
      },
      outsideMonth: {
        color: 'colors.buttonSupport',
        background: 'colors.textNegative',
      },
      dayInRange: {
        background: 'colors.secondary',
        color: 'colors.textLabel',
      },
      disabled: {
        color: 'colors.reserve02',
      },
      dropdown: {
        background: 'colors.secondaryBackground',
        color: 'colors.primary',
      },
      monthOption: {
        hoverBackground: 'colors.buttonSupport',
        hoverColor: 'colors.primary',
      },
      board: {
        background: 'colors.secondaryBackground',
      },
      selectedDay: {
        color: 'colors.textLabel',
        background: 'colors.reserve02',
      },
    },
  },
  dropdown: {
    background: 'colors.primaryBackground',
    border: 'colors.borderSupport',
    listItem: {
      focus: {
        background: 'colors.borderSupport',
        color: 'colors.primaryVariant',
      },
      selected: {
        background: 'colors.secondary',
      },
    },
    valid: {
      border: 'colors.success',
    },
    invalid: {
      border: 'colors.error',
    },
  },
  dot: {
    border: 'colors.buttonSupport',
    valid: {
      background: 'colors.success',
      border: 'colors.success',
    },
    invalid: {
      background: 'colors.error',
      border: 'colors.error',
    },
  },
  fieldLabel: {
    color: 'colors.textLabel',
    errorMessage: {
      color: 'colors.error',
    },
  },
  popupLoader: {
    background: 'colors.textPositive',
    dots: 'colors.secondary',
  },
  toggleButton: {
    color: 'colors.borderSupport',
  },
  tooltip: {
    color: 'colors.secondary',
    hoverColor: 'colors.secondary|chroma|darker|0.7',
    iconTextColor: 'colors.textNegative',
    content: {
      border: 'colors.reserve02',
      color: 'colors.textPositive',
    },
  },
  residentialData: {
    timeAtAddress: {
      tabButton: {
        color: 'colors.borderSupport',
        selected: 'colors.box',
      },
    },
    background: 'colors.primaryBackground',
    border: 'colors.primaryLink',
  },
  checkbox: {
    light: {
      hoverBorder: 'colors.secondary',
      border: 'colors.secondaryBackground',
      checked: {
        background: 'colors.secondary',
        border: 'colors.secondary',
        hoverBorder: 'colors.secondary',
        color: 'colors.textLabel',
      },
      disabled: {
        color: 'colors.reserve02',
        border: 'colors.reserve02',
        background: 'transparent',
      },
      invalid: {
        background: 'colors.error',
      },
    },
    dark: {
      hoverBorder: 'colors.secondary',
      border: 'colors.reserve01',
      background: 'colors.secondaryBackground',
      checked: {
        background: 'colors.secondary',
        border: 'colors.secondary',
        hoverBorder: 'colors.secondary',
        color: 'colors.textLabel',
      },
      disabled: {
        color: 'colors.reserve02',
        border: 'colors.reserve02',
        background: 'transparent',
      },
      invalid: {
        background: 'colors.error',
      },
    },
  },
  flow: {
    background: 'colors.primaryBackground',
  },
  footer: {
    background: 'colors.box',
    label: {
      color: 'colors.textPositive',
      underline: 'colors.textPositive',
    },
    legend: {
      color: 'colors.textPositive',
    },
  },
  header: {
    background: 'colors.box',
    number: {
      color: 'colors.textPositive',
    },
  },
  input: {
    required: {
      color: 'colors.error',
    },
    extraDetails: {
      line: 'colors.borderSupport',
      color: 'colors.primary',
    },
    border: 'colors.reserve03',
    background: 'colors.textNegative',
    neutral: {
      border: 'colors.borderSupport',
    },
    valid: {
      border: 'colors.success',
    },
    invalid: {
      border: 'colors.error',
    },
    disabled: {
      background: 'colors.secondaryBackground|chroma|alpha|0.5',
      color: 'colors.textLabel|chroma|alpha|0.5',
    },
    placeholder: {
      color: 'colors.primary',
    },
  },
  autocomplete: {
    placeholder: {
      color: 'colors.primary',
    },
    listItem: {
      focus: {
        background: 'colors.borderSupport',
        color: 'colors.primaryVariant',
      },
      selected: {
        background: 'colors.secondary',
      },
    },
  },
  label: {
    light: {
      color: 'colors.textNegative',
    },
    dark: {
      color: 'colors.textPositive',
    },
  },
  list: {
    item: {
      background: 'colors.buttonSupport|chroma|brighten|0.4',
      oddBackground: 'colors.primaryBackground',
      hoverBackground: 'colors.buttonSupport',
      selectedBackground: 'colors.secondary',
      border: 'colors.buttonSupport',
    },
  },
  main: {
    background: 'colors.primaryBackground',
  },
  panel: {
    background: 'colors.textLabel',
    color: 'colors.textNegative',
    hoverColor: 'colors.secondary',
    paragraph: {
      color: 'colors.readableNegative',
    },
  },
  popup: {
    content: {
      background: 'colors.primaryBackground',
    },
    close: {
      background: 'colors.secondary',
    },
    footer: {
      border: 'colors.borderSupport',
    },
  },
  progressBar: {
    background: 'colors.secondaryBackground',
    notDone: {
      background: 'colors.reserve01', //chroma("colors.secondary").brighten(2).hex()
    },
    done: {
      background: 'colors.success',
      color: 'colors.box',
    },
    iconWithLabel: {
      progressLine: {
        color: 'colors.borderSupport',
      },
      isActive: {
        color: 'colors.box',
      },
      notActive: {
        color: 'colors.reserve01',
      },
      circle: {
        background: 'colors.primaryVariant',
      },
      activeCircle: {
        background: 'colors.secondary',
      },
    },
  },
  seperator: {
    background: 'colors.borderSupport',
  },
  login: {
    color: 'colors.textLabel',
    link: {
      color: 'colors.primaryLink',
    },
  },
  statementTypeSelector: {
    button: {
      checkMark: {
        color: 'colors.textLabel',
      },
    },
    or: {
      color: 'colors.textPositive',
      background: 'colors.reserve02',
    },
    text: {
      valid: {
        color: 'colors.success|chroma|darker|0.9',
      },
    },
  },
  linkAccountDetails: {
    icons: {
      background: 'colors.primaryBackground',
      backgroundSelected: 'colors.secondary',
      border: 'colors.borderSupport',
    },
  },
  openBanking: {
    banksFrame: {
      border: 'colors.reserve02',
    },
  },
  confirmationPopup: {
    link: {
      color: 'colors.primaryLink',
    },
  },
  generalError: {
    errorSignBackground: 'colors.primary',
    errorSign: 'colors.secondaryVariant',
  },
  notFound: {
    context: 'colors.primary',
  },
  general: {
    focus: 'colors.focus',
    success: 'colors.success',
    error: 'colors.error',
    warning: 'colors.neutralAlert',
  },
  address: {
    firstLine: {
      background: 'colors.secondary',
    },
    enterManual: {
      background: 'colors.secondary',
      editIcon: 'colors.none',
    },
  },
  marketingPreferences: {
    line: 'colors.reserve01',
    subtitle: 'colors.primary',
  },
  carousel: {
    dots: {
      border: 'colors.primary',
      background: 'colors.textNegative',
      active: 'colors.textPositive',
    },
  },
  radioButtons: {
    radio: {
      unchecked: {
        color: 'colors.textPositive',
      },
      checked: {
        color: 'colors.box',
      },
      hover: {
        backgroundColor: 'colors.borderSupport',
      },
      ripple: {
        color: 'colors.box',
      },
    },
  },
  idVerification: {
    verifyIdentity: {
      button: {
        background: 'colors.box',
        color: 'colors.textNegative',
      },
    },
    photo: {
      border: 'colors.borderSupport',
      boxShadow: 'colors.primary',
      captured: {
        border: 'colors.borderSupport',
        boxShadow: 'colors.primary',
        status: {
          success: 'colors.success',
          error: 'colors.error',
        },
        checked: {
          background: '#689F38',
        },
      },
      notCaptured: {
        border: 'colors.box',
        boxShadow: 'colors.primary',
      },
    },
    capture: {
      objectFrame: {
        error: {
          border: 'colors.error',
        },
        success: {
          border: 'colors.success',
        },
      },
    },
  },
  materialIdVerification: {
    // TODO: Check with Paz how to declare colors without to change old widgets
    progress: {
      success: '#387f3c',
      error: '#E52828',
    },
    photo: {
      color: '#000',
      background: '#FFF',
      selected: {
        background: '#FFF',
        color: '#003B5A',
      },
    },
  },
  mobileVerification: {
    status: {
      OK: {
        color: 'colors.textPositive',
      },
      WRONG_CODE: {
        color: 'colors.error',
      },
      CODE_EXPIRED: {
        color: 'colors.textPositive',
      },
      TOO_MANY_FAIL_ATTEMPTS: {
        color: 'colors.textPositive',
      },
    },
  },
  switch: {
    primary: {
      circleColor: 'colors.buttonSupport',
      checkedTrack: 'colors.buttonSupport|chroma|brighten|0.5',
      unCheckedTrack: 'colors.primary',
    },
    secondary: {
      circleColor: 'colors.box',
      checkedTrack: 'colors.box|chroma|brighten|0.5',
      unCheckedTrack: 'colors.secondary',
    },
  },
  materialFab: {
    circular: {
      primary: {
        backgroundColor: 'colors.materialPrimary',
        color: 'colors.textNegative',
        disabled: {
          backgroundColor: 'colors.materialPrimaryDisabled',
          color: 'colors.textPositive',
          border: 'transparent',
        },
      },
    },
  },
  materialButtonWrapper: {
    width: '28rem',
  },
  materialButton: {
    contained: {
      primary: {
        backgroundColor: 'colors.materialPrimary',
        color: 'colors.textNegative',
        disabled: {
          backgroundColor: 'colors.materialPrimaryDisabled',
          color: 'colors.textPositive',
          border: 'transparent',
        },
      },
    },
    text: {
      primary: {
        backgroundColor: 'transparent',
        color: 'colors.materialPrimaryLink',
        border: 'transparent',
        textDecoration: 'underline',
        disabled: {
          backgroundColor: 'transparent',
          color: 'colors.textPositive',
          border: 'transparent',
        },
      },
    },
    outlined: {
      primary: {
        backgroundColor: 'colors.materialSecondary',
        color: 'colors.materialPrimary',
        border: 'colors.materialPrimary',
        disabled: {
          backgroundColor: 'colors.materialSecondary',
          color: 'colors.textPositive',
          border: 'colors.textPositive',
        },
      },
    },
  },
  materialContinue: {
    contained: {
      primary: {
        backgroundColor: 'colors.materialPrimary',
        color: 'colors.textNegative',
        border: 'transparent',
        disabled: {
          backgroundColor: 'colors.materialPrimaryDisabled',
          color: 'colors.textPositive',
          border: 'transparent',
        },
      },
    },
    text: {
      primary: {
        backgroundColor: 'transparent',
        color: 'colors.materialPrimaryLink',
        border: 'transparent',
        disabled: {
          backgroundColor: 'transparent',
          color: 'colors.textPositive',
          border: 'transparent',
        },
      },
    },
    outlined: {
      primary: {
        backgroundColor: 'colors.materialSecondary',
        color: 'colors.materialPrimary',
        border: 'colors.materialPrimary',
        disabled: {
          backgroundColor: 'colors.materialSecondary',
          color: 'colors.textPositive',
          border: 'colors.textPositive',
        },
      },
    },
  },
  materialTypography: {
    h1: {
      fontSize: '3.2rem',
      fontWeight: 400,
      color: 'colors.materialPrimary',
    },
    h2: {
      fontSize: '2.4rem',
      fontWeight: 400,
      color: 'colors.materialPrimary',
      sm: {
        fontSize: '3.2rem',
      },
    },
    body1: {
      fontSize: '1.5rem',
      color: 'colors.textPositive',
      fontWeight: 400,
    },
    body2: {
      fontSize: '1.3rem',
      color: 'colors.textPositive',
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    caption: {
      fontSize: '1.1rem',
      fontWeight: 400,
    },
    button: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
  },
  materialCheckbox: {
    primary: {
      border: 'colors.textPositive',
      color: 'colors.textPositive',
      checked: {
        color: 'colors.textPositive',
        border: 'colors.success',
      },
      disabled: {
        color: 'colors.materialPrimaryDisabled',
        border: 'colors.materialPrimaryDisabled',
      },
    },
  },
  materialRadio: {
    primary: {
      border: 'colors.textPositive',
      color: 'colors.textPositive',
      checked: {
        color: 'colors.textPositive',
        border: 'colors.materialPrimary',
      },
      disabled: {
        color: 'colors.materialPrimaryDisabled',
        border: 'colors.materialPrimaryDisabled',
      },
    },
  },
  materialAppBar: {
    backgroundColor: 'colors.materialAppBarBackground',
    color: 'colors.textNegative',
    loginSignupButton: {
      color: 'colors.textNegative',
    },
    height: '60px',
    sm: {
      height: '60px',
      color: 'colors.textNegative',
      backgroundColor: 'colors.materialAppBarBackground',
    },
  },
  materialSelect: {
    label: {
      color: 'colors.primary',
    },
    input: {
      borderColor: 'colors.primary',
    },
    menuItem: {
      selected: {
        backgroundColor: 'colors.materialPrimary|chroma|alpha|0.098',
      },
    },
  },
  materialDatePicker: {
    label: {
      color: 'colors.primary',
    },
    input: {
      borderColor: 'colors.primary',
    },
    toolbar: {
      backgroundColor: 'colors.materialPrimary',
    },
    daySelected: {
      color: 'colors.textNegative',
      backgroundColor: 'colors.materialPrimary',
    },
  },
  materialLogin: {
    backgroundColor: 'colors.white',
    color: 'colors.materialPrimary',
    title: {
      color: 'colors.materialPrimary',
    },
    closedAppsTitle: {
      color: 'colors.textMuted',
      weight: 400,
    },
    backButton: {
      color: 'colors.primaryVariant',
    },
    card: {
      other: {
        color: 'colors.materialPrimaryDisabled',
      },
      expired: {
        color: 'colors.error',
      },
      color: 'colors.materialPrimary',
      backgroundColor: 'colors.white',
      cancelled: { color: 'colors.primary' },
      completed: { color: 'colors.success' },
      borderClosed: { color: 'colors.materialPrimaryDisabled' },
    },
    summary: {
      backgroundColor: 'colors.primaryBackground',
      button: {
        backgroundColor: 'colors.error',
      },
    },
  },
  materialUploadDocument: {
    icon: {
      color: 'colors.materialPrimary',
    },
    border: {
      color: 'colors.buttonSupport',
    },
    backgroundColor: 'colors.primaryBackground',
  },
  materialPersonsSignificantControl: {
    listItem: {
      backgroundColor: 'colors.none',
      color: 'colors.primary',
      selected: {
        backgroundColor: 'colors.materialPrimary',
        color: 'colors.materialSecondary',
      },
      hover: {
        backgroundColor: 'colors.materialPrimary',
        color: 'colors.materialSecondary',
      },
    },
    radio: {
      color: 'colors.primary',
      selected: {
        color: 'colors.materialPrimary',
      },
      disabled: {
        color: 'colors.materialPrimaryDisabled',
      },
    },
  },
  MaterialShareholders: {
    NoDataMessage: {
      background: 'colors.none',
      color: 'colors.primary',
    },
  },
  Signature: {
    border: 'colors.primaryVariant',
  },
  materialTooltip: {
    icon: {
      color: 'colors.primaryVariant',
    },
    backgroundColor: 'colors.primaryVariant',
    color: 'colors.white',
  },
  materialDialog: {
    title: {
      color: 'colors.materialPrimary',
    },
    content: {
      color: 'colors.textMuted',
    },
  },
  materialOptionalOffers: {
    repaymentRate: {
      color: 'colors.darkBlue',
      fontWeight: 500,
    },
  },
  materialDataGrid: {
    background: {
      color: 'colors.tableBackground',
    },
    rowCell: {
      color: 'colors.secondaryBackground',
    },
    loader: {
      color: 'colors.tableSecondary',
    },
  },
  materialProgressBar: {
    label: {
      fontWeight: '600',
    },
    icon: {
      color: 'colors.materialPrimary',
      backgroundColor: 'colors.white',
      width: '3.5rem',
      height: '3.5rem',
    },
    connector: {
      color: 'colors.materialPrimary',
      border: 'colors.buttonSupport',
    },
    colors: {
      primary: '',
      grey: 'colors.buttonSupport',
    },
  },
  screenWrapper: {
    content: {
      color: 'colors.textPositive',
    },
    background: 'colors.none',
    back: {
      color: 'colors.textPositive',
      border: 'colors.textPositive',
    },
    border: {
      color: 'colors.buttonSupport',
    },
    topImage: {
      maxWidth: '140px',
    },
  },
  materialTextField: {
    label: {
      color: 'colors.primary',
    },
    input: {
      borderColor: 'colors.primary',
    },
  },
  materialAddressLookup: {
    label: {
      color: 'colors.primary',
    },
    input: {
      borderColor: 'colors.primary',
    },
  },
  materialTabs: {
    color: 'colors.primary',
    border: 'colors.secondary',
    active: {
      color: 'colors.primaryLink',
      backgroundColor: 'colors.primary',
      border: 'colors.secondary',
      indicator: 'colors.none',
    },
  },
  materialSwitch: {
    thumb: {
      color: 'colors.primary',
    },
    track: {
      uncheckedTrack: {
        color: 'colors.secondary',
      },
      checkedTrack: {
        color: 'colors.primary',
      },
    },
  },
};
